import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

function HeroGridPattern() {
  const patternId = `idemgo-hero-grid-${useId().replace(/:/g, '')}`;
  const cellSize = 64;
  const squares = [
    [9, 2],
    [12, 4],
    [15, 1],
    [17, 5],
    [20, 3],
  ];

  return (
    <svg
      className="pointer-events-none absolute -inset-[20%] h-[140%] w-[140%] stroke-zinc-300/35"
      aria-hidden="true"
      fill="none"
      preserveAspectRatio="none"
      style={{
        maskImage:
          'radial-gradient(ellipse at 68% 42%, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.58) 42%, rgba(0,0,0,0.20) 72%, transparent 96%)',
        transform: 'rotate(-8deg) skewY(8deg) scale(1.12)',
        transformOrigin: '68% 42%',
      }}
    >
      <defs>
        <pattern id={patternId} width={cellSize} height={cellSize} x="-1" y="-1" patternUnits="userSpaceOnUse">
          <path d={`M.5 ${cellSize}V.5H${cellSize}`} strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} strokeWidth="0" />
      <g className="fill-zinc-300/18 stroke-0">
        {squares.map(([x, y]) => (
          <rect key={`${x}-${y}`} width={cellSize - 1} height={cellSize - 1} x={x * cellSize} y={y * cellSize} />
        ))}
      </g>
    </svg>
  );
}

const rotatingTargets = ['customer', 'business', 'employee', 'partner', 'device', 'transaction'];

const typingTimings = {
  type: 120,
  delete: 75,
  fullPause: 1700,
  nextPause: 450,
};

export default function Hero() {
  const [targetIndex, setTargetIndex] = useState(0);
  const [typedWord, setTypedWord] = useState(rotatingTargets[0]);
  const [typingPhase, setTypingPhase] = useState('pause');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (mediaQuery.matches) {
      setTargetIndex(0);
      setTypedWord(rotatingTargets[0]);
      setTypingPhase('pause');
      return undefined;
    }

    const currentWord = rotatingTargets[targetIndex];
    const timeoutDelay =
      typingPhase === 'pause'
        ? typingTimings.fullPause
        : typingPhase === 'deleting'
          ? typingTimings.delete
          : typedWord.length === 0
            ? typingTimings.nextPause
            : typingTimings.type;

    const timeoutId = window.setTimeout(() => {
      if (typingPhase === 'pause') {
        setTypingPhase('deleting');
        return;
      }

      if (typingPhase === 'deleting') {
        if (typedWord.length <= 1) {
          setTypedWord('');
          setTargetIndex((current) => (current + 1) % rotatingTargets.length);
          setTypingPhase('typing');
          return;
        }

        setTypedWord((current) => current.slice(0, -1));
        return;
      }

      if (typedWord.length >= currentWord.length) {
        setTypingPhase('pause');
        return;
      }

      setTypedWord(currentWord.slice(0, typedWord.length + 1));
    }, timeoutDelay);

    return () => window.clearTimeout(timeoutId);
  }, [targetIndex, typedWord, typingPhase]);

  const displayedWord = typedWord || '\u00A0';

  return (
    <section className="idemgo-hero relative overflow-hidden bg-white">
      <HeroGridPattern />

      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white via-white/10 to-transparent" />

      <div className="relative z-10 px-6">
        <div className="mx-auto flex min-h-[560px] max-w-4xl flex-col justify-center md:min-h-[600px]">
          <div className="text-center">
            <p className="text-[12px] font-mono uppercase tracking-widest text-zinc-500 mt-16">
              VERIFICATION INFRASTRUCTURE
            </p>
          </div>

          <h1 className="mt-8 text-center font-display font-bold tracking-tight text-[#111111]">
            <span className="block text-5xl leading-[1.06] sm:text-6xl md:text-7xl">
              Verify users.
            </span>

            <span className="mt-2 flex flex-col items-center text-5xl leading-[1.08] sm:text-6xl lg:flex-row lg:justify-center lg:gap-[0.18em] md:text-7xl">
            <span>Know your</span>

            <span className="mt-3 inline-flex items-baseline rounded-[3px] bg-zinc-950 px-2.5 py-0.9 text-white lg:mt-0">
              <span className="font-bold">{displayedWord}.</span>
            </span>
          </span>
          </h1>

          <p className="mx-auto mt-6 max-w-[620px] text-center text-[15px] leading-relaxed text-zinc-600 sm:text-base md:text-lg">
            Identity verification, onboarding flows, and digital signatures for modern platforms. EU-hosted,
            audit-ready, and privacy-first by design.
          </p>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400/80">
            <span>EU-hosted</span>
            <span className="hidden h-px w-2.5 bg-zinc-300/50 sm:inline" aria-hidden="true" />
            <span>MRZ validation</span>
            <span className="hidden h-px w-2.5 bg-zinc-300/50 sm:inline" aria-hidden="true" />
            <span>Face match</span>
            <span className="hidden h-px w-2.5 bg-zinc-300/50 sm:inline" aria-hidden="true" />
            <span>Audit trail</span>
          </div>

          <div className="mt-6 flex justify-center gap-3">
            <Link
              to="/signup"
              data-testid="hero-signup-cta"
              className="inline-flex items-center gap-2 mt-4 rounded-sm bg-black px-5 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-zinc-800"
            >
              Start for free <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Link>
            <Link
              to="/pricing"
              data-testid="hero-signup-cta"
              className="inline-flex items-center gap-2 mt-4 rounded-sm border border-zinc-300 bg-white px-5 py-3 text-sm font-medium text-zinc-900 transition-colors duration-200 hover:border-zinc-400 hover:bg-zinc-50"
            >
              View pricing
            </Link>
          </div>

        </div>
      </div>

      <div className="idemgo-hero-fade pointer-events-none absolute inset-x-0 bottom-0 h-32" />
    </section>
  );
}
